import React, { useEffect, useState } from 'react';
import withStyles from '@mui/styles/withStyles';
import styles from './JwtAuthentication.styles';
import Content from 'studio/components/Content/Content';
import ChevronRightIcon from '@mui/icons-material/ChevronRightRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreRounded';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Switch from '@mui/material/Switch';
import services from 'services';
import TextArea from 'studio/components/TextArea';
import TextField from 'studio/components/TextField';
import { Controller, useForm } from 'react-hook-form/dist/index.ie11';
import { yupResolver } from '@hookform/resolvers/dist/ie11/yup';
import * as yup from 'yup';
import { useLoader } from 'components/loaders/FadeLoader';
import { useMessage } from 'studio/components/Message';
import { trackEvent } from 'utils/eventTracking';
import FormControlLabel from '@mui/material/FormControlLabel';
import CodeSnippet from 'studio/components/CodeSnippet/CodeSnippet';
import Collapse from '@mui/material/Collapse';
import PageTitle from 'studio/components/PageTitle';
import UserAttributeMappings, {
  getSchema as getUserAttributeMappingsSchema
} from 'studio/components/UserAttributeMappings';
import { getUserTypesWithAdmin } from 'studio/constants';
import { sortBy } from 'utils/arrayUtils';
import VpnKeyIcon from '@mui/icons-material/VpnKeyRounded';
import { veezooUrls } from 'config/constants';
import Select from 'studio/components/Select';
import HelpButton from 'studio/components/HelpButton';
import { isHttpUrl } from 'studio/helpers';
import Tab from '@mui/material/Tab';
import Tabs from 'studio/components/Tabs/Tabs';
import _ from 'lodash';

// the available parameter modes of a JWT authentication configuration
const parameterModes = {
  url: 'url',
  cookie: 'cookie'
};

const JwtAuthentication = ({ classes, user, languages }) => {
  useEffect(() => {
    trackEvent('Admin SSO JWT Opened');
  }, []);

  const { withLoader, loading } = useLoader();
  const { successMessage, errorMessage } = useMessage();

  const publicKeyModes = { publicKey: 'publicKey', jwks: 'jwks' };

  // the JWT authentication configuration
  const [jwtAuthenticationConfiguration, setJwtAuthenticationConfiguration] = useState(undefined);
  // the available user roles
  const [roles, setRoles] = useState(null);
  // specifies whether JWT authentication is allowed for the organization
  const [jwtAuthenticationIsAllowed, setJwtAuthenticationIsAllowed] = useState(false);
  // the currently active mode for the public key (single public key or JWKS)
  const [publicKeyMode, setPublicKeyMode] = useState(publicKeyModes.publicKey);
  // specifies whether the help section for key generation is shown
  const [showKeyHelp, setShowKeyHelp] = useState(false);

  // retrieves the existing JWT authentication configuration
  const getJwtAuthenticationConfiguration = async () => {
    const response = await services.getJwtAuthenticationConfiguration();
    const jwtAuthenticationConfiguration = response.data || null;
    if (!response.success && response.response.status === 402) {
      setJwtAuthenticationIsAllowed(false);
    } else {
      setJwtAuthenticationIsAllowed(true);
    }
    setJwtAuthenticationConfiguration(jwtAuthenticationConfiguration);
  };

  const getRoles = async () => {
    const response = await services.getRoles();
    if (response.success) {
      const roles = sortBy(
        response.data.data.map(role => role.data),
        role => role.name
      );
      setRoles(roles);
    }
  };

  // replaces the JWT authentication configuration
  const replaceJwtAuthenticationConfiguration = async jwtAuthenticationConfiguration => {
    // only include one of 'publicKey' or 'jwksUrl', depending on the current public key mode
    jwtAuthenticationConfiguration = {
      ...jwtAuthenticationConfiguration,
      publicKey: publicKeyMode === publicKeyModes.publicKey ? jwtAuthenticationConfiguration.publicKey : null,
      jwksUrl: publicKeyMode === publicKeyModes.jwks ? jwtAuthenticationConfiguration.jwksUrl : null
    };
    // explicitly unset the discarded field: this is needed since otherwise it stays around in the form data
    // for some reason (likely because the text field is hidden) and can unexpectedly reappear again
    setValue('publicKey', jwtAuthenticationConfiguration.publicKey);
    setValue('jwksUrl', jwtAuthenticationConfiguration.jwksUrl);

    const response = await withLoader(services.replaceJwtAuthenticationConfiguration(jwtAuthenticationConfiguration));
    if (response.success) {
      setJwtAuthenticationConfiguration(response.data);
      successMessage('The JWT authentication configuration has successfully been updated');
    } else {
      const message =
        response?.response?.data?.data?.message || 'There was an error updating the JWT authentication configuration';
      errorMessage(message);
    }
  };

  // initially retrieves the JWT authentication configuration and roles
  useEffect(() => {
    getJwtAuthenticationConfiguration();
    getRoles();
  }, []);

  // the user types, including "Admin" if the current user is admin
  const userTypes = getUserTypesWithAdmin(user);

  // validates the specified public key by checking that its header / footer conforms with the supported formats
  // (might be improved at some point to parse the key using 'window.crypto.subtle.importKey')
  const isValidPublicKey = key =>
    // SPKI format
    (key?.startsWith('-----BEGIN PUBLIC KEY-----') && key?.endsWith('-----END PUBLIC KEY-----')) ||
    // PKCS #1 format
    (key?.startsWith('-----BEGIN RSA PUBLIC KEY-----') && key?.endsWith('-----END RSA PUBLIC KEY-----')) ||
    // X.509 certificate format
    (key?.startsWith('-----BEGIN CERTIFICATE-----') && key?.endsWith('-----END CERTIFICATE-----'));

  // the validation rules for the form fields
  const schema = yup.object().shape(
    _.pickBy(
      {
        isEnabled: yup.bool(),
        parameterMode: yup.string().oneOf(Object.values(parameterModes)).required(),
        parameterName: yup
          .string()
          .notRequired()
          .nullable()
          .transform(name => (name?.trim() ? name : null)),
        publicKey: yup
          .string()
          .required()
          .test('is-valid-rsa-key', 'The RSA public key is invalid', value => !value || isValidPublicKey(value)),
        jwksUrl: yup
          .string()
          .required()
          .test('is-valid-jwks-url', 'The JWKS URL is invalid', value => !value || isHttpUrl(value)),
        userAttributeMappings: getUserAttributeMappingsSchema(user)
      },
      // filter out 'publicKey' or 'jwksUrl' depending on the current public key mode
      (value, key) => (publicKeyMode === 'publicKey' ? key !== 'jwksUrl' : key !== 'publicKey')
    )
  );

  const form = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      isEnabled: false,
      parameterMode: 'url',
      parameterName: null,
      publicKey: null,
      jwksUrl: null
    },
    // set to false since it causes issues with array fields inside tabs
    shouldUnregister: false
  });
  const { handleSubmit, control, errors, reset, formState, watch, setValue } = form;
  const data = watch();

  // resets the form whenever the JWT authentication configuration changes
  useEffect(() => {
    if (jwtAuthenticationConfiguration) {
      // set the current public key mode depending on whether the configuration contains a 'jwksUrl' or not
      setPublicKeyMode(jwtAuthenticationConfiguration.data.jwksUrl ? publicKeyModes.jwks : publicKeyModes.publicKey);
      reset(jwtAuthenticationConfiguration.data);
    }
  }, [jwtAuthenticationConfiguration]);

  // returns the relative URL for the documentation given the specified hash
  const docsUrl = hash => `/single-sign-on/jwt${hash ? `#${hash}` : ''}`;

  return (
    <Content loading={jwtAuthenticationConfiguration === undefined} hasUnsavedChanges={loading || formState.isDirty}>
      <div className={classes.root}>
        <PageTitle icon={VpnKeyIcon}>JWT Authentication</PageTitle>

        {!jwtAuthenticationIsAllowed && (
          <p>
            Single sign-on with JWT is only available for <b>Enterprise</b> subscription plans. Check our{' '}
            <Link href={veezooUrls.pricing} target="_blank" rel="noreferrer">
              pricing
            </Link>{' '}
            and{' '}
            <Link href={veezooUrls.contact} target="_blank" rel="noreferrer">
              get in touch
            </Link>{' '}
            with us to enable it!
          </p>
        )}
        {jwtAuthenticationIsAllowed && (
          <>
            <Typography variant="body1" gutterBottom>
              Configure single sign-on with JWT. Read the{' '}
              <Link href={`${veezooUrls.docs}${docsUrl()}`} target="_blank" rel="noreferrer">
                documentation
              </Link>{' '}
              for more information on how to configure it.
            </Typography>

            <form autoComplete="off" className={classes.form}>
              <Controller
                control={control}
                name="isEnabled"
                render={({ ref, value }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        inputRef={ref}
                        onChange={event => {
                          const isEnabled = event.target.checked;
                          handleSubmit(newJwtAuthenticationConfiguration =>
                            replaceJwtAuthenticationConfiguration({ ...newJwtAuthenticationConfiguration, isEnabled })
                          )();
                        }}
                        checked={value}
                      />
                    }
                    label={'JWT authentication is ' + (value ? 'enabled' : 'disabled')}
                  />
                )}
              />

              <Typography variant="h2" gutterBottom>
                Parameter
                <HelpButton docsUrl={docsUrl('parameter')} small style={{ marginLeft: 10 }} />
              </Typography>
              <Typography variant="body1" gutterBottom>
                Specify how you pass the JWT as a parameter to Veezoo.
              </Typography>
              <div className={classes.parameterContainer}>
                <div className={classes.parameterMode}>
                  <Controller
                    control={control}
                    as={Select}
                    name="parameterMode"
                    label="Parameter mode"
                    description="How you provide the JWT to Veezoo"
                    values={[parameterModes.url, parameterModes.cookie]}
                    labels={['URL parameter', 'Cookie']}
                    error={!!errors.parameterMode}
                  />
                </div>
                <Controller
                  as={TextField}
                  control={control}
                  name="parameterName"
                  label="Parameter name"
                  description={
                    <>
                      The name of the {data.parameterMode === parameterModes.url ? 'URL parameter' : 'cookie'} (defaults
                      to <code>jwt</code> if not specified)
                    </>
                  }
                  placeholder="jwt"
                  error={!!errors.parameterName}
                />
              </div>

              <Typography variant="h2" gutterBottom>
                Public Key
                <HelpButton
                  docsUrl="/embedded/embedded-guide/#3-configure-a-public-key-to-setup-jwt-json-web-tokens"
                  small
                  style={{ marginLeft: 10 }}
                />
              </Typography>
              <Typography variant="body1" gutterBottom>
                JWTs are secured via a digital signature based on an RSA public/private key pair. Configure either a
                single public key or a JWKS (JSON Web Key Set).
              </Typography>
              <Tabs
                value={Object.keys(publicKeyModes).indexOf(publicKeyMode)}
                onChange={(_, index) => setPublicKeyMode(publicKeyModes[Object.keys(publicKeyModes)[index]])}
              >
                <Tab label="Public Key" />
                <Tab label="JWKS" />
              </Tabs>
              <div hidden={publicKeyMode !== 'publicKey'}>
                <Typography
                  variant="body1"
                  gutterBottom
                  onClick={() => setShowKeyHelp(!showKeyHelp)}
                  className={classes.keyHelp}
                >
                  {showKeyHelp ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                  Follow the steps below to generate a 2048-bit RSA key pair
                </Typography>
                <Collapse in={showKeyHelp}>
                  <Typography variant="body1" gutterBottom>
                    1. Generate your RSA private key using the following command:
                  </Typography>
                  <CodeSnippet>
                    openssl genpkey -algorithm RSA -pkeyopt rsa_keygen_bits:2048 -out private-key.pem
                  </CodeSnippet>
                  <Typography variant="body1" gutterBottom>
                    2. Extract the corresponding public key with this command:
                  </Typography>
                  <CodeSnippet>openssl rsa -pubout -in private-key.pem -out public-key.pem</CodeSnippet>
                </Collapse>
                <Typography variant="body1" gutterBottom>
                  Sign the JWTs you generate with the private key and configure the corresponding public key here for
                  Veezoo to verify your signed JWTs.
                </Typography>
                <Controller
                  as={TextArea}
                  control={control}
                  name="publicKey"
                  label="Public key"
                  description="Enter the public key in one of the supported PEM-encoded formats: SPKI, PKCS #1 or X.509 certificate"
                  placeholder={'-----BEGIN PUBLIC KEY-----\n...\n-----END PUBLIC KEY-----'}
                  resize="vertical"
                  error={!!errors.publicKey}
                />
              </div>
              <div hidden={publicKeyMode !== 'jwks'} className={classes.jwks}>
                <Typography variant="body1" gutterBottom>
                  If you are using a JWKS (JSON Web Key Set), configure the URL of the JWKS JSON endpoint serving the
                  keys.
                </Typography>
                <Controller
                  as={TextField}
                  control={control}
                  name="jwksUrl"
                  label="JWKS URL"
                  placeholder="https://mycompany.com/jwks.json"
                  resize="vertical"
                  error={!!errors.jwksUrl}
                />
              </div>

              <Typography variant="h2" gutterBottom>
                User Attributes
              </Typography>
              <Typography variant="body1" gutterBottom>
                Configure user attributes based on claims from the JWT.
              </Typography>

              <UserAttributeMappings
                fieldName="userAttributeMappings"
                user={user}
                userTypes={userTypes}
                languages={languages}
                roles={roles}
                form={form}
              />

              <div className={classes.actions}>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handleSubmit(replaceJwtAuthenticationConfiguration)}
                  disabled={loading || !formState.isDirty}
                >
                  Save
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </Content>
  );
};

export default withStyles(styles)(JwtAuthentication);
